/**
 * StudioMultiSelectTools — contextual actions for a 2+ component selection.
 *
 * Shown in the right dock in place of the single-component Inspector. Every
 * action routes through a pure builder in `studioLayoutEdits` and commits with
 * an explicit undo label; a builder that would move nothing returns null and no
 * command is recorded. Required singletons and generation blocks are protected
 * by the builders (custom-only delete/duplicate), so no fleet-count invariant is
 * broken here — resizing the fleet stays in its own sanctioned transaction.
 */
import React, { useState } from 'react';
import {
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  Copy,
  PinOff,
  RotateCcw,
  RotateCw,
  Trash2,
} from 'lucide-react';

import type {
  GenerationLayoutComponentPolygon,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import type { GenerationLayoutConfiguration } from '../../../../types/generationImpact';
import {
  alignComponents,
  deleteCustomComponents,
  distributeComponents,
  duplicateCustomComponents,
  moveComponentsToUnplaced,
  nudgeComponents,
  resolveCustomComponent,
  rotateComponents,
  type StudioAlignEdge,
  type StudioDistributeAxis,
} from './studioLayoutEdits';

export interface StudioMultiSelectToolsProps {
  components: ReadonlyArray<GenerationLayoutComponentPolygon>;
  activeLayout: GenerationLayoutConfiguration;
  anchor: { lat: number; lng: number };
  readOnly: boolean;
  onCommit: (label: string, patch: Partial<GenerationLayoutConfiguration>) => void;
  onClearSelection: () => void;
}

const ALIGN_BUTTONS: ReadonlyArray<{ edge: StudioAlignEdge; label: string; testId: string }> = [
  { edge: 'left', label: 'Left', testId: 'studio-align-left' },
  { edge: 'hcenter', label: 'Center', testId: 'studio-align-hcenter' },
  { edge: 'right', label: 'Right', testId: 'studio-align-right' },
  { edge: 'top', label: 'Top', testId: 'studio-align-top' },
  { edge: 'vmiddle', label: 'Middle', testId: 'studio-align-vmiddle' },
  { edge: 'bottom', label: 'Bottom', testId: 'studio-align-bottom' },
];

const ALIGN_LABELS: Record<StudioAlignEdge, string> = {
  left: 'Align left',
  hcenter: 'Align horizontal centres',
  right: 'Align right',
  top: 'Align top',
  vmiddle: 'Align vertical centres',
  bottom: 'Align bottom',
};

const cellClass =
  'min-h-9 border border-border bg-card px-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary transition-colors hover:bg-background hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 max-[599px]:min-h-11';

export const StudioMultiSelectTools: React.FC<StudioMultiSelectToolsProps> = ({
  components,
  activeLayout,
  anchor,
  readOnly,
  onCommit,
  onClearSelection,
}) => {
  const [step, setStep] = useState('5');
  const stepM = Number(step.trim());
  const stepValid = Number.isFinite(stepM) && stepM > 0;

  const ids = components.map(component => component.rectangle.id);
  const hasCustom = components.some(component => resolveCustomComponent(activeLayout, component.rectangle.id) !== null);
  const hasPinned = components.some(component => component.rectangle.placement === 'pinned');
  const canDistribute = components.length >= 3;

  const run = (label: string, patch: Partial<GenerationLayoutConfiguration> | null): void => {
    if (!patch || readOnly) return;
    onCommit(label, patch);
  };

  const align = (edge: StudioAlignEdge): void =>
    run(ALIGN_LABELS[edge], alignComponents(activeLayout, components, anchor, edge));
  const distribute = (axis: StudioDistributeAxis): void =>
    run(axis === 'horizontal' ? 'Distribute horizontally' : 'Distribute vertically',
      distributeComponents(activeLayout, components, anchor, axis));
  const rotate = (deg: number): void =>
    run(`Rotate ${deg > 0 ? '+' : ''}${deg}°`, rotateComponents(activeLayout, components, anchor, { mode: 'by', deg }));
  const nudge = (dxM: number, dyM: number): void => {
    if (!stepValid) return;
    run('Nudge selection', nudgeComponents(activeLayout, components, anchor, dxM * stepM, dyM * stepM));
  };

  return (
    <div className="min-w-0 space-y-3 p-3" data-testid="studio-multiselect-tools">
      <p className="text-[11px] font-bold uppercase tracking-wider text-text-primary">
        {components.length} selected
      </p>

      <section aria-label="Align" className="space-y-1">
        <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Align</p>
        <div className="grid grid-cols-3 gap-1">
          {ALIGN_BUTTONS.map(button => (
            <button
              key={button.edge}
              type="button"
              disabled={readOnly}
              onClick={() => align(button.edge)}
              className={cellClass}
              data-testid={button.testId}
            >
              {button.label}
            </button>
          ))}
        </div>
      </section>

      <section aria-label="Distribute" className="space-y-1">
        <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Distribute</p>
        <div className="grid grid-cols-2 gap-1">
          <button type="button" disabled={readOnly || !canDistribute} onClick={() => distribute('horizontal')} className={cellClass} data-testid="studio-distribute-horizontal" title={canDistribute ? 'Even horizontal gaps' : 'Select 3+ to distribute'}>
            <AlignHorizontalJustifyCenter className="mx-auto h-3.5 w-3.5" aria-hidden /> Horizontal
          </button>
          <button type="button" disabled={readOnly || !canDistribute} onClick={() => distribute('vertical')} className={cellClass} data-testid="studio-distribute-vertical" title={canDistribute ? 'Even vertical gaps' : 'Select 3+ to distribute'}>
            <AlignVerticalJustifyCenter className="mx-auto h-3.5 w-3.5" aria-hidden /> Vertical
          </button>
        </div>
      </section>

      <section aria-label="Rotate" className="space-y-1">
        <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Rotate</p>
        <div className="grid grid-cols-2 gap-1">
          <button type="button" disabled={readOnly} onClick={() => rotate(-90)} className={cellClass} data-testid="studio-rotate-ccw">
            <RotateCcw className="mx-auto h-3.5 w-3.5" aria-hidden /> 90° CCW
          </button>
          <button type="button" disabled={readOnly} onClick={() => rotate(90)} className={cellClass} data-testid="studio-rotate-cw">
            <RotateCw className="mx-auto h-3.5 w-3.5" aria-hidden /> 90° CW
          </button>
        </div>
      </section>

      <section aria-label="Nudge" className="space-y-1">
        <div className="flex items-center justify-between">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Nudge</p>
          <label className="flex items-center gap-1 text-[9px] uppercase text-text-muted">
            Step
            <input
              type="number"
              min={0.1}
              step={0.5}
              value={step}
              onChange={(event) => setStep(event.target.value)}
              disabled={readOnly}
              className="w-14 border border-border bg-background p-1 text-[10px] text-text-primary disabled:opacity-50 rounded-md"
              data-testid="studio-nudge-step"
              aria-label="Nudge step (metres)"
            />
            m
          </label>
        </div>
        <div className="grid grid-cols-4 gap-1">
          <button type="button" disabled={readOnly || !stepValid} onClick={() => nudge(-1, 0)} className={cellClass} data-testid="studio-nudge-left" aria-label="Nudge west">←</button>
          <button type="button" disabled={readOnly || !stepValid} onClick={() => nudge(0, 1)} className={cellClass} data-testid="studio-nudge-up" aria-label="Nudge north">↑</button>
          <button type="button" disabled={readOnly || !stepValid} onClick={() => nudge(0, -1)} className={cellClass} data-testid="studio-nudge-down" aria-label="Nudge south">↓</button>
          <button type="button" disabled={readOnly || !stepValid} onClick={() => nudge(1, 0)} className={cellClass} data-testid="studio-nudge-right" aria-label="Nudge east">→</button>
        </div>
      </section>

      {!readOnly && (
        <section aria-label="Selection actions" className="flex flex-wrap gap-1 border-t border-border pt-2">
          <button
            type="button"
            disabled={!hasCustom}
            onClick={() => run('Duplicate custom objects', duplicateCustomComponents(activeLayout, ids, anchor))}
            className={`${cellClass} flex items-center gap-1`}
            data-testid="studio-multiselect-duplicate"
            title={hasCustom ? 'Duplicate the selected custom objects' : 'Only custom objects can be duplicated'}
          >
            <Copy className="h-3 w-3" aria-hidden /> Duplicate
          </button>
          <button
            type="button"
            disabled={!hasPinned}
            onClick={() => run('Move to Unplaced', moveComponentsToUnplaced(activeLayout, ids))}
            className={`${cellClass} flex items-center gap-1`}
            data-testid="studio-multiselect-unplace"
            title={hasPinned ? 'Return pinned equipment to the packer' : 'Nothing pinned to unplace'}
          >
            <PinOff className="h-3 w-3" aria-hidden /> Unplace
          </button>
          <button
            type="button"
            disabled={!hasCustom}
            onClick={() => {
              const patch = deleteCustomComponents(activeLayout, ids);
              if (!patch) return;
              onCommit('Delete custom objects', patch);
              onClearSelection();
            }}
            className={`${cellClass} flex items-center gap-1 border-danger text-danger`}
            data-testid="studio-multiselect-delete"
            title={hasCustom ? 'Delete the selected custom objects' : 'Only custom objects can be deleted'}
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        </section>
      )}
    </div>
  );
};
